import { intervalToDuration, isAfter, isValid, parse, parseISO } from 'date-fns'
import { format, toZonedTime } from 'date-fns-tz'

export const MonthDateFormat = {
    DISPLAY: 'MM/yyyy',
    STORAGE: 'MM-yyyy',
    DATE_OBJECT: 'dateobject',
} as const

export type MonthDateFormatType =
    (typeof MonthDateFormat)[keyof typeof MonthDateFormat]

function expandYear(shortYear: number): number {
    return shortYear < 50 ? 2000 + shortYear : 1900 + shortYear
}

function parseMonthYear(input: string): { month: number; year: number } | null {
    let match: RegExpMatchArray | null

    if ((match = input.match(/^(\d{4})$/))) {
        return { month: 1, year: parseInt(match[1]) }
    }

    if ((match = input.match(/^(\d{1,2})[/-](\d{4})$/))) {
        const month = parseInt(match[1])
        if (month < 1 || month > 12) return null
        return { month, year: parseInt(match[2]) }
    }

    if ((match = input.match(/^(\d{1,2})[/-](\d{2})$/))) {
        const month = parseInt(match[1])
        if (month < 1 || month > 12) return null
        return { month, year: expandYear(parseInt(match[2])) }
    }

    if ((match = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/))) {
        const month = parseInt(match[1])
        if (month < 1 || month > 12) return null
        return { month, year: parseInt(match[3]) }
    }

    if ((match = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/))) {
        const month = parseInt(match[1])
        if (month < 1 || month > 12) return null
        return { month, year: expandYear(parseInt(match[3])) }
    }

    return null
}

export function toMonthDate(
    input: Date | string | null | undefined,
    outputFormat: 'dateobject'
): Date | null
export function toMonthDate(
    input: Date | string | null | undefined,
    outputFormat: 'MM/yyyy' | 'MM-yyyy'
): string
export function toMonthDate(
    input: Date | string | null | undefined,
    outputFormat: MonthDateFormatType
): Date | string | null {
    if (input == null || input === '')
        return outputFormat === 'dateobject' ? null : ''

    let month: number
    let year: number

    if (input instanceof Date) {
        if (!isValid(input)) return outputFormat === 'dateobject' ? null : ''
        month = input.getMonth() + 1
        year = input.getFullYear()
    } else {
        const parsed = parseMonthYear(input)
        if (!parsed) {
            throw new Error(`toMonthDate: unrecognized format "${input}"`)
        }
        month = parsed.month
        year = parsed.year
    }

    const date = new Date(year, month - 1, 1)

    if (outputFormat === 'dateobject') return date
    return format(date, outputFormat)
}

export const formatDateForSchedule = (startDateTime, endDateTime) => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const startDate = toZonedTime(parseISO(startDateTime), userTimeZone)
    const endDate = toZonedTime(parseISO(endDateTime), userTimeZone)

    const dayFormat = format(startDate, 'EEEE, MMMM d', {
        timeZone: userTimeZone,
    })
    const timeFormat = `${format(startDate, 'h:mm', { timeZone: userTimeZone })} – ${format(endDate, 'h:mma', { timeZone: userTimeZone })}`

    return `${dayFormat} • ${timeFormat}`
}

export const parsedDateAndMonth = (inputDateString) => {
    const dateStr = parse(inputDateString, 'M/d/yyyy, h:mm:ss a', new Date())
    const formattedDate = format(dateStr, 'd MMM yyyy')
    return formattedDate
}

// API is returning mm-yyyy format, so we need to convert it to yyyy-mm-ddTtt or new Date() will return invalid date
export const MMyyyyToValidDateString = (date: string) => {
    if (date?.includes('-')) {
        return date?.split('-').reverse().concat('01T12:00:00').join('-')
    } else if (date?.includes('/')) {
        return date?.split('/').reverse().concat('01T12:00:00').join('-')
    } else {
        return date
    }
}

export const isEndDateAfterStartForMMyyyy = (
    startDate: string,
    endDate: string
) => {
    const start = new Date(MMyyyyToValidDateString(startDate))
    const end = new Date(MMyyyyToValidDateString(endDate))

    return isAfter(end, start)
}

/**
 * Formats a date string into a desired format, with a fallback value if the
 * date string can't be parsed.
 */
export const formatDateWithFallback = (
    /** The unparsed date value to format. */
    date: string | null | undefined,
    /** The value to return when the provided date value can't be parsed. */
    fallbackValue: string,
    /** The desired format for the parsed date. Uses the [format options provided by date-fns](https://date-fns.org/v4.1.0/docs/format). */
    desiredFormat: string = 'MM/yyyy',
    /** The format of the unparsed date value the function should attempt to parse into a valid date object (i.e. the format of the date as stored in the database). Uses the [format options provided by date-fns](https://date-fns.org/v4.1.0/docs/format). */
    expectedFormat: string = 'MM-yyyy'
) => {
    const parsedDate = date ? parse(date, expectedFormat, new Date()) : null
    return parsedDate && isValid(parsedDate)
        ? format(parsedDate, desiredFormat)
        : fallbackValue
}

/**
 * Formats a duration between two dates into a human-readable string. Returns
 * `undefined` if the start date can't be parsed to allow you to provide a
 * fallback value.
 */
export const formatDuration = (
    /** The unparsed start date value of the duration to format. */
    start: string | null | undefined,
    /** The unparsed end date value of the duration to format. */
    end: string | null | undefined,
    isCurrent: boolean = false,
    /** The format of the unparsed date values the function should attempt to parse into valid date objects (i.e. the format of the dates as stored in the database). Uses the [format options provided by date-fns](https://date-fns.org/v4.1.0/docs/format). */
    expectedFormat: string = 'MM-yyyy'
) => {
    const endDateFallback = isCurrent ? new Date() : null
    const startDate = start ? parse(start, expectedFormat, new Date()) : null
    const endDate = end
        ? parse(end, expectedFormat, new Date())
        : endDateFallback

    if (!startDate || !isValid(startDate) || !endDate || !isValid(endDate)) {
        return undefined
    }

    const duration = intervalToDuration({
        start: startDate,
        end: endDate,
    })

    const formattedDurationParts: string[] = []
    if (duration.years) formattedDurationParts.push(`${duration.years}y`)
    if (duration.months) formattedDurationParts.push(`${duration.months}m`)

    return formattedDurationParts.join(' ')
}

export function formatAuditDate(
    prefix: 'Created' | 'Updated',
    name?: string,
    date?: string
): string {
    return `${prefix} ${new Date(date ?? '').toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
    })} by ${name || 'Unknown'}`
}

// Compact relative-time string (e.g. "5m ago", "3h ago", "Apr 12").
// Falls back to "just now" for sub-minute and an absolute month-day for >7d.
// Future-dated inputs return "just now" rather than negative values.
export function formatRelativeTime(date: Date): string {
    const ms = Date.now() - date.getTime()
    if (ms < 60_000) return 'just now'
    const minutes = Math.floor(ms / 60_000)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days < 7) return `${days}d ago`
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
